import React, { useState, useCallback } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio, Checkbox, DatePicker } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useM309Query } from './hooks/useM309Query';
import type { ResultRow, UnitSummary, QueryParams } from '../../services/m309Api';
import './M309Form.css';

const M309Form: React.FC = () => {
  const { loading, data, summary, total, queried, noData, runQuery, reset } = useM309Query();

  // Filter state
  const [startWarehouse, setStartWarehouse] = useState('');
  const [lastWarehouse, setLastWarehouse] = useState('');
  const [startProduct, setStartProduct] = useState('');
  const [lastProduct, setLastProduct] = useState('');
  const [startBatch, setStartBatch] = useState('');
  const [lastBatch, setLastBatch] = useState('');
  const [startBoard, setStartBoard] = useState('');
  const [lastBoard, setLastBoard] = useState('');
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [lastDate, setLastDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [processCode, setProcessCode] = useState('******');
  const [zeroStock, setZeroStock] = useState(2);
  const [showAmount, setShowAmount] = useState(false);

  const handleQuery = useCallback(() => {
    const params: QueryParams = {
      startWarehouse: startWarehouse.trim() || undefined,
      lastWarehouse: lastWarehouse.trim() || undefined,
      startProduct: startProduct.trim() || undefined,
      lastProduct: lastProduct.trim() || undefined,
      startBatch: startBatch.trim() || undefined,
      lastBatch: lastBatch.trim() || undefined,
      startBoard: startBoard.trim() || undefined,
      lastBoard: lastBoard.trim() || undefined,
      startDate: startDate ? startDate.format('YYYY-MM-DD') : undefined,
      lastDate: lastDate ? lastDate.format('YYYY-MM-DD') : undefined,
      processCode: processCode.trim() !== '******' ? processCode.trim() : undefined,
      zeroStock,
      showAmount,
    };
    runQuery(params);
  }, [startWarehouse, lastWarehouse, startProduct, lastProduct, startBatch, lastBatch,
      startBoard, lastBoard, startDate, lastDate, processCode, zeroStock, showAmount, runQuery]);

  const handleReset = () => {
    setStartWarehouse(''); setLastWarehouse('');
    setStartProduct(''); setLastProduct('');
    setStartBatch(''); setLastBatch('');
    setStartBoard(''); setLastBoard('');
    setStartDate(null); setLastDate(dayjs());
    setProcessCode('******'); setZeroStock(2); setShowAmount(false);
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const amtHeaders = showAmount ? '<th>庫存金額</th><th>平均單價</th>' : '';
    const amtCells = (r: ResultRow) => showAmount
      ? `<td class="right">${r.stock_amount.toLocaleString()}</td><td class="right">${r.avg_unit_price.toFixed(3)}</td>`
      : '';
    win.document.write(`<!DOCTYPE html><html><head><title>板號庫位批號庫存</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:8mm}}</style></head><body>
<h2>板號庫位批號庫存</h2>
<table><thead><tr><th>板號</th><th>庫位</th><th>庫位名稱</th><th>產品代號</th><th>批號</th><th>色號</th><th>色名</th><th>單位</th><th>庫存量</th>${amtHeaders}<th>入庫日期</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.board_no}</td><td>${r.warehouse_code}</td><td>${r.warehouse_name}</td><td>${r.product_code}</td><td>${r.batch_no}</td><td>${r.color_code}</td><td>${r.color_name}</td><td>${r.unit}</td><td class="right">${r.stock_qty.toLocaleString()}</td>${amtCells(r)}<td>${r.entry_date ? r.entry_date.substring(0, 10) : ''}</td></tr>`).join('')}
</tbody></table>
${summary.length > 0 ? `<h3>單位小計</h3><table><thead><tr><th>單位</th><th>合計庫存量</th>${showAmount ? '<th>合計金額</th><th>平均單價</th>' : ''}</tr></thead><tbody>
${summary.map(s => `<tr><td>${s.unit}</td><td class="right">${s.total_qty.toLocaleString()}</td>${showAmount ? `<td class="right">${s.total_amount.toLocaleString()}</td><td class="right">${s.avg_unit_price.toFixed(3)}</td>` : ''}</tr>`).join('')}
</tbody></table>` : ''}
</body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  // Build columns
  const columns: ColumnsType<ResultRow> = [
    { title: '板號', dataIndex: 'board_no', key: 'board_no', width: 70, fixed: 'left' },
    { title: '庫位', dataIndex: 'warehouse_code', key: 'warehouse_code', width: 70 },
    { title: '庫位名稱', dataIndex: 'warehouse_name', key: 'warehouse_name', width: 120 },
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', width: 130 },
    { title: '批號', dataIndex: 'batch_no', key: 'batch_no', width: 130 },
    { title: '色號', dataIndex: 'color_code', key: 'color_code', width: 60 },
    { title: '色名', dataIndex: 'color_name', key: 'color_name', width: 120 },
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 50 },
    { title: '庫存量', dataIndex: 'stock_qty', key: 'stock_qty', width: 90, align: 'right' as const,
      render: (v: number) => v?.toLocaleString() },
  ];

  if (showAmount) {
    columns.push(
      { title: '庫存金額', dataIndex: 'stock_amount', key: 'stock_amount', width: 110, align: 'right' as const,
        render: (v: number) => v?.toLocaleString() },
      { title: '平均單價', dataIndex: 'avg_unit_price', key: 'avg_unit_price', width: 100, align: 'right' as const,
        render: (v: number) => v?.toFixed(3) },
    );
  }

  columns.push(
    { title: '入庫日期', dataIndex: 'entry_date', key: 'entry_date', width: 100,
      render: (v: string | null) => v ? v.substring(0, 10) : '' },
    { title: '研發架號', dataIndex: 'rd_shelf_no', key: 'rd_shelf_no', width: 70 },
    { title: '幅寬', dataIndex: 'width', key: 'width', width: 60, align: 'right' as const,
      render: (v: number) => v ? v.toFixed(1) : '' },
    { title: '重量', dataIndex: 'weight', key: 'weight', width: 60, align: 'right' as const,
      render: (v: number) => v ? v.toFixed(1) : '' },
  );

  // Summary table columns
  const summaryColumns: ColumnsType<UnitSummary> = [
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 60 },
    { title: '合計庫存量', dataIndex: 'total_qty', key: 'total_qty', width: 120, align: 'right' as const,
      render: (v: number) => v?.toLocaleString() },
  ];
  if (showAmount) {
    summaryColumns.push(
      { title: '合計金額', dataIndex: 'total_amount', key: 'total_amount', width: 120, align: 'right' as const,
        render: (v: number) => v?.toLocaleString() },
      { title: '平均單價', dataIndex: 'avg_unit_price', key: 'avg_unit_price', width: 100, align: 'right' as const,
        render: (v: number) => v?.toFixed(3) },
    );
  }

  return (
    <PermissionGuard module={Modules.M309} action="query">
      <div className="m309-container">
        <div className="m309-header">
          <h2>板號庫位批號庫存</h2>
          {queried && !noData && <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          {/* Row 1: Warehouse + Product */}
          <div className="m309-filter-row" style={{ marginBottom: 8 }}>
            <div className="m309-filter-item">
              <label>庫位代號 [從]</label>
              <Input value={startWarehouse} onChange={e => setStartWarehouse(e.target.value)} maxLength={5}
                placeholder="起始" style={{ width: 110 }} allowClear />
            </div>
            <div className="m309-filter-item">
              <label>[至]</label>
              <Input value={lastWarehouse} onChange={e => setLastWarehouse(e.target.value)} maxLength={5}
                placeholder="結束" style={{ width: 110 }} allowClear />
            </div>
            <div className="m309-filter-item">
              <label>產品代號 [從]</label>
              <Input value={startProduct} onChange={e => setStartProduct(e.target.value)} maxLength={12}
                placeholder="起始" style={{ width: 140 }} allowClear />
            </div>
            <div className="m309-filter-item">
              <label>[至]</label>
              <Input value={lastProduct} onChange={e => setLastProduct(e.target.value)} maxLength={12}
                placeholder="結束" style={{ width: 140 }} allowClear />
            </div>
          </div>
          {/* Row 2: Batch + Board */}
          <div className="m309-filter-row" style={{ marginBottom: 8 }}>
            <div className="m309-filter-item">
              <label>批號 [從]</label>
              <Input value={startBatch} onChange={e => setStartBatch(e.target.value)} maxLength={12}
                placeholder="起始" style={{ width: 140 }} allowClear />
            </div>
            <div className="m309-filter-item">
              <label>[至]</label>
              <Input value={lastBatch} onChange={e => setLastBatch(e.target.value)} maxLength={12}
                placeholder="結束" style={{ width: 140 }} allowClear />
            </div>
            <div className="m309-filter-item">
              <label>板號 [從]</label>
              <Input value={startBoard} onChange={e => setStartBoard(e.target.value)} maxLength={5}
                placeholder="起始" style={{ width: 110 }} allowClear />
            </div>
            <div className="m309-filter-item">
              <label>[至]</label>
              <Input value={lastBoard} onChange={e => setLastBoard(e.target.value)} maxLength={5}
                placeholder="結束" style={{ width: 110 }} allowClear />
            </div>
          </div>
          {/* Row 3: Date + ProcessCode + Options + Actions */}
          <div className="m309-filter-row">
            <div className="m309-filter-item">
              <label>入庫日期 [從]</label>
              <DatePicker value={startDate} onChange={v => setStartDate(v)} style={{ width: 130 }} />
            </div>
            <div className="m309-filter-item">
              <label>[至]</label>
              <DatePicker value={lastDate} onChange={v => setLastDate(v)} style={{ width: 130 }} />
            </div>
            <div className="m309-filter-item">
              <label>工繳代號</label>
              <Input value={processCode} onChange={e => setProcessCode(e.target.value)} maxLength={6}
                placeholder="******" style={{ width: 100 }} />
            </div>
            <div className="m309-filter-item">
              <label>庫存量為零</label>
              <Radio.Group value={zeroStock} onChange={e => setZeroStock(e.target.value)}>
                <Radio value={1}>顯示</Radio>
                <Radio value={2}>隱藏</Radio>
              </Radio.Group>
            </div>
            <div className="m309-filter-item">
              <label>&nbsp;</label>
              <Checkbox checked={showAmount} onChange={e => setShowAmount(e.target.checked)}>列印單價金額</Checkbox>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </div>
        </Card>

        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}

        {queried && !noData && summary.length > 0 && (
          <Card size="small" title="單位小計" style={{ marginBottom: 16 }} className="m309-summary-table">
            <Table<UnitSummary> columns={summaryColumns} dataSource={summary} rowKey="unit"
              size="small" pagination={false} />
          </Card>
        )}

        {queried && !noData && (
          <Table<ResultRow> columns={columns} dataSource={data}
            rowKey={(r, i) => `${r.board_no}-${r.warehouse_code}-${r.product_code}-${r.batch_no}-${i}`}
            loading={loading} size="small"
            pagination={{ pageSize: 100, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'],
              showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 1400 }} />
        )}
      </div>
    </PermissionGuard>
  );
};

export default M309Form;
